import Phaser from 'phaser';
import {
  ComponentType,
  ComponentCategory,
  ComponentDef,
  COMPONENT_DEFS,
  WeaponTarget,
  MINE_CONFIG,
  EXCAVATOR_CONFIG,
} from '@/components/ComponentDefs';
import {
  BehaviorRule,
  ConditionType,
  ActionType,
  TargetModifier,
} from '@/behaviors/BehaviorDefs';
import { ROBOT_TEMPLATES, RobotTemplate, getTemplates, getMutableTemplates, saveTemplatesToDisk } from '@/config/RobotTemplates';
import { evolveComponentDef } from '@/entities/Robot';
import { EventBus, GameEvents } from '@/events/EventBus';
import { DesignStore } from '@/systems/DesignStore';
import '@/config/ComponentEvolution'; // ensure evolution map is registered before getEvolutionCost calls
import { SPEED_REFERENCE_MASS, TILE_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT, COMPONENT_SPACING } from '@/config/GameConfig';
import { calculateStability, stabilitySpeedMultiplier } from '@/systems/StabilitySystem';
import { SoundManager, MusicTrack } from '@/systems/SoundManager';
import { createComponentVisual, CATEGORY_COLORS } from '@/systems/ComponentIcons';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { TECH, createTechButton, createSmallTechButton, showTextInputDialog, drawTechRect } from '@/ui/TechUI';
import { createButton, createSmallButton } from '@/ui/UISwitch';
import { getGridComponentTypes } from '@/ui/BehaviorValidation';
import { GOD } from '@/config/GodMode';
import { t } from '@/i18n/I18n';
import { checkNameEasterEgg, getMaxColumnHeight, getStackOverflowWarning, incrementEasterEggStat } from '@/systems/EasterEggs';
import { isDevMode, DEV_MODE_CHANGED } from '@/systems/DevConsole';
import { getSequenceIds, getSequence } from '@/cinematic/CinematicDirector';
import { SceneCinematicWatcher } from '@/cinematic/SceneCinematicWatcher';

// ── Extracted modules ──
import { GridManager } from './workshop/GridManager';
import { PaletteController } from './workshop/PaletteController';
import { PlacementValidator } from './workshop/PlacementValidator';
import { RulesEditorPanel } from './workshop/RulesEditorPanel';
import { TemplateEditorPanel } from './workshop/TemplateEditorPanel';

// ── Exported interfaces (unchanged) ──

export interface RobotDesign {
  components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[];
  rules: BehaviorRule[];
  cost: number;
}

export interface WorkshopEditData {
  components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[];
  rules: BehaviorRule[];
  name: string;
  editingRobotId?: string;
  paletteScrollX?: number;
  /** Modification fee for editing a placed robot (passed by UIScene) */
  modFee?: number;
  /** Original robot cost before editing (for diff calculation) */
  originalCost?: number;
  /** Player's current resources (to show affordability) */
  playerResources?: number;
}

// Re-export edge helpers so external code that imports them from WorkshopScene still works
export { edgesCompatible, getRotatedEdges, getTransformedEdges, getTransformedForbiddenEdges } from './workshop/PlacementValidator';

/** Generate sensible default behavior rules based on which components are present. */
function generateDefaultRules(types: ComponentType[]): BehaviorRule[] {
  const rules: BehaviorRule[] = [];
  let p = 1;

  const hasWeapon = types.some(t => COMPONENT_DEFS[t].category === ComponentCategory.WEAPON);
  const hasExcavator = types.includes(ComponentType.EXCAVATOR);
  const hasRecharge = types.includes(ComponentType.RECHARGE_MODULE);
  const hasRepair = types.includes(ComponentType.REPAIR_MODULE);
  const hasExplosive = types.includes(ComponentType.EXPLOSIVE_CHARGE);
  const hasMobility = types.some(t =>
    t === ComponentType.WHEEL || t === ComponentType.WING ||
    t === ComponentType.TURBINE || t === ComponentType.STAB_LEGS || t === ComponentType.JUMPER,
  );

  // Support actions first (repair/recharge allies)
  if (hasRepair) {
    rules.push({ priority: p++, condition: ConditionType.BASE_DAMAGED, action: ActionType.REPAIR_BASE });
    rules.push({ priority: p++, condition: ConditionType.ALLY_NEARBY_DAMAGED, action: ActionType.REPAIR_ALLY });
  }
  if (hasRecharge) {
    rules.push({ priority: p++, condition: ConditionType.ALLY_LOW_ENERGY, action: ActionType.RECHARGE_ALLY });
  }

  // Combat
  if (hasExplosive) {
    rules.push({ priority: p++, condition: ConditionType.ENEMY_CLOSE, action: ActionType.EXPLODE });
  }
  if (hasWeapon) {
    rules.push({ priority: p++, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST });
  }

  // Harvesting
  if (hasExcavator) {
    rules.push({ priority: p++, condition: ConditionType.RESOURCE_DETECTED, action: ActionType.HARVEST });
  }

  // Fallback
  rules.push({ priority: p++, condition: ConditionType.ALWAYS, action: hasMobility ? ActionType.PATROL : ActionType.HOLD_POSITION });

  return rules;
}

// ── Layout constants ──
const MARGIN = 12;
const RULES_PANEL_W = 370;
const STATS_PANEL_W = 220;
const TITLE_H = 56;
const PANEL_BG = 0x0d0d1a;
const PANEL_BORDER = 0x333366;

// ── WorkshopScene ──

export class WorkshopScene extends Phaser.Scene {
  // Modules
  private gridManager!: GridManager;
  private palette!: PaletteController;
  private placement!: PlacementValidator;
  private rulesEditor!: RulesEditorPanel;

  // Stats
  private statsTexts: Phaser.GameObjects.Text[] = [];
  private warningsText!: Phaser.GameObjects.Text;
  private modFeeText?: Phaser.GameObjects.Text;
  private totalCost = 0;

  // Design
  private designName = '';
  private nameText!: Phaser.GameObjects.Text;
  private static designCounter = 0;
  private editingRobotId: string | null = null;
  private modFee = 0;         // modification fee for editing placed robot
  private originalCost = 0;   // original robot cost (for diff)
  private playerResources = Infinity; // player's current resources
  private originalComponents: WorkshopEditData['components'] = []; // for change detection

  // Tooltip
  private tooltip!: Phaser.GameObjects.Container;
  private tooltipTitle!: Phaser.GameObjects.Text;
  private tooltipDesc!: Phaser.GameObjects.Text;
  private tooltipStats!: Phaser.GameObjects.Text;
  private tooltipBg!: Phaser.GameObjects.Rectangle;

  // Campaign mode
  private isCampaign = false;


  // Cinematic trigger watcher
  private cinematicWatcher: SceneCinematicWatcher | null = null;

  // Dev template editor (visible only in devMode)
  private templateEditor: TemplateEditorPanel | null = null;

  // Campaign hint
  private campaignHint?: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'WorkshopScene' });
  }

  /** Snapshot the current grid + rules + name so we can pass them to scene.restart() */
  private snapshotEditData(): WorkshopEditData {
    const { rows, cols } = this.gridManager.getDimensions();
    const components: WorkshopEditData['components'] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const type = this.gridManager.getCell(r, c);
        if (type === null) continue;
        components.push({
          type,
          lx: c * COMPONENT_SPACING,
          ly: r * COMPONENT_SPACING,
          rotation: this.gridManager.getRotation(r, c) || undefined,
          flipX: this.gridManager.getFlipX(r, c) || undefined,
          flipY: this.gridManager.getFlipY(r, c) || undefined,
          evoLevel: this.gridManager.getEvoLevel(r, c) || undefined,
        });
      }
    }
    return {
      components,
      rules: this.rulesEditor.rules.map(r => ({ ...r })),
      name: this.designName,
      editingRobotId: this.editingRobotId ?? undefined,
      paletteScrollX: this.palette.paletteScrollX,
    };
  }

  // ══════════════════════════════════════════
  //  CREATE
  // ══════════════════════════════════════════

  create(data?: WorkshopEditData): void {
    this.add.rectangle(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_WIDTH, CANVAS_HEIGHT, 0x08081a, 0.98).setDepth(0);
    SoundManager.playMusic(MusicTrack.WORKSHOP);

    // Reset state
    this.statsTexts = [];
    this.totalCost = 0;
    this.editingRobotId = data?.editingRobotId ?? null;
    this.modFee = data?.modFee ?? 0;
    this.originalCost = data?.originalCost ?? 0;
    this.playerResources = data?.playerResources ?? Infinity;
    this.originalComponents = data?.components?.map(c => ({ ...c })) ?? [];

    // Detect campaign mode
    this.isCampaign = !!this.registry.get('currentLevelNode');
    DesignStore.mode = this.isCampaign ? 'campaign' : 'challenge';

    // ── Instantiate modules ──
    this.gridManager = new GridManager(this);
    this.gridManager.recomputeLayout(ProgressionManager.gridCols, ProgressionManager.gridRows);

    const layout = this.gridManager.getLayout();

    this.palette = new PaletteController(this, this.isCampaign, {
      getAvailableFunds: () => this.getAvailableFunds(),
      spawnCostAnim: (amount, x, y) => this.spawnCostAnim(amount, x, y),
      showCampaignHint: (lx, ly) => this.showCampaignHint(lx, ly),
      showTooltip: (x, y, def, locked, evoLevel) => this.showTooltip(x, y, def, locked, evoLevel),
      hideTooltip: () => this.hideTooltip(),
      snapshotEditData: () => this.snapshotEditData(),
    }, layout.paletteY);

    const placementCallbacks = {
      recalcStats: () => this.recalcStats(),
      refreshRuleDisplays: () => this.rulesEditor.refreshRuleDisplays(),
    };
    this.placement = new PlacementValidator(this, this.gridManager, placementCallbacks);

    this.rulesEditor = new RulesEditorPanel(
      this, layout.rulesX, RULES_PANEL_W, layout.topSectionH, TITLE_H,
      { getGridTypes: () => this.getGridTypes() },
    );

    // Rules init: editing an existing robot → keep its rules; new design → generate defaults
    if (data?.rules?.length) {
      for (const r of data.rules) this.rulesEditor.rules.push({ ...r });
    } else {
      const defaults = generateDefaultRules(this.getGridTypes());
      for (const r of defaults) this.rulesEditor.rules.push(r);
    }

    // Design name
    if (data?.name) {
      this.designName = data.name;
    } else {
      WorkshopScene.designCounter++;
      this.designName = `Robot-${WorkshopScene.designCounter}`;
    }

    this.createTitle();
    this.gridManager.createGrid((r, c, pointer) => this.onGridCellPointerDown(r, c, pointer));
    this.gridManager.createGridExpandButtons(this.isCampaign, () => this.snapshotEditData());
    this.palette.createPalette();
    this.createStatsPanel();
    this.rulesEditor.createRulesPanel();
    this.createButtons();
    EventBus.on(GameEvents.COMPONENT_UNLOCKED, this.palette.schedulePaletteRebuild, this);
    EventBus.on(GameEvents.CURRENCY_CHANGED, this.palette.onCurrencyChanged, this);
    EventBus.on(GameEvents.RESOURCES_UPDATED, this.palette.onCurrencyChanged, this);
    this.events.once('shutdown', () => {
      EventBus.off(GameEvents.COMPONENT_UNLOCKED, this.palette.schedulePaletteRebuild, this);
      EventBus.off(GameEvents.CURRENCY_CHANGED, this.palette.onCurrencyChanged, this);
      EventBus.off(GameEvents.RESOURCES_UPDATED, this.palette.onCurrencyChanged, this);
    });
    this.createTooltip();
    this.setupDragDrop();

    // Restore palette scroll position
    if (data?.paletteScrollX != null && data.paletteScrollX !== 0) {
      this.palette.paletteScrollX = 0;
      this.palette.scrollPalette(data.paletteScrollX);
    }

    // Pre-fill grid if editing an existing design
    if (data?.components?.length) {
      this.gridManager.batchMode = true;
      this.gridManager.loadComponentsToGrid(
        data.components, true,
        (r, c, type, rot, skipV, skipC, evo) => this.placement.placeOnGrid(r, c, type, rot, skipV, skipC, evo),
        () => {},
      );
      this.gridManager.batchMode = false;
      this.gridManager.drawConnections();
      this.recalcStats();
    }

    // Cinematic trigger watcher
    this.cinematicWatcher = new SceneCinematicWatcher(this, 'WorkshopScene');
    this.cinematicWatcher.fire('workshopOpen');
    EventBus.on(GameEvents.WORKSHOP_DESIGN_COMPLETE, this.onDesignCompleteForCinematic, this);
    EventBus.on(GameEvents.WORKSHOP_EDIT_COMPLETE, this.onDesignCompleteForCinematic, this);

    // Dev template editor (only in devMode)
    if (isDevMode()) {
      this.createTemplateEditor();
    }
    const onDevModeForTemplateEditor = (on: boolean) => {
      if (on && !this.templateEditor) this.createTemplateEditor();
      if (!on && this.templateEditor) { this.templateEditor.destroy(); this.templateEditor = null; }
    };
    EventBus.on(DEV_MODE_CHANGED, onDevModeForTemplateEditor);

    this.events.once('shutdown', () => {
      EventBus.off(GameEvents.WORKSHOP_DESIGN_COMPLETE, this.onDesignCompleteForCinematic, this);
      EventBus.off(GameEvents.WORKSHOP_EDIT_COMPLETE, this.onDesignCompleteForCinematic, this);
      EventBus.off(DEV_MODE_CHANGED, onDevModeForTemplateEditor);
      this.cinematicWatcher?.destroy();
      this.cinematicWatcher = null;
      this.templateEditor?.destroy();
      this.templateEditor = null;
      // Destroy modules
      this.gridManager.destroy();
      this.palette.destroy();
      this.placement.destroy();
      this.rulesEditor.destroy();
    });

    // Show challenge mode intro dialog (once per session)
    if (!this.isCampaign && !localStorage.getItem('rfd_challenge_intro_seen')) {
      this.showChallengeIntro();
    }
  }

  private onDesignCompleteForCinematic = (): void => {
    this.cinematicWatcher?.fire('designComplete');
  };

  // ══════════════════════════════════════════
  //  GRID CELL POINTER DOWN (coordination)
  // ══════════════════════════════════════════

  private onGridCellPointerDown(r: number, c: number, pointer: Phaser.Input.Pointer): void {
    if (pointer.rightButtonDown()) {
      SoundManager.play('clickCancel');
      this.gridManager.removeFromGrid(r, c, {
        recalcStats: () => this.recalcStats(),
        refreshRuleDisplays: () => this.rulesEditor.refreshRuleDisplays(),
      });
    } else if (this.gridManager.getCell(r, c) !== null && !this.palette.dragGhost) {
      this.placement.gridDragSource = { r, c };
      this.placement.gridDragType = this.gridManager.getCell(r, c);
      this.placement.gridDragRotation = this.gridManager.getRotation(r, c);
      this.placement.gridDragEvoLevel = this.gridManager.getEvoLevel(r, c);
      this.placement.gridDragFlipX = this.gridManager.getFlipX(r, c);
      this.placement.gridDragFlipY = this.gridManager.getFlipY(r, c);
      this.placement.gridDragStartTime = this.time.now;
      this.placement.gridDragActive = false;
    } else if (this.palette.selectedType && !this.palette.dragGhost) {
      this.placement.placeOnGrid(r, c, this.palette.selectedType, 0, false, false, this.palette.selectedEvoLevel);
    }
  }

  // ══════════════════════════════════════════
  //  TITLE BAR
  // ══════════════════════════════════════════

  private createTitle(): void {
    this.add.text(CANVAS_WIDTH / 2, 10, t('workshopScene.title'), {
      fontSize: '20px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0).setDepth(1);

    this.add.text(CANVAS_WIDTH / 2, 34, t('workshopScene.instructions'), {
      fontSize: '10px', color: '#555577', fontFamily: 'monospace',
    }).setOrigin(0.5, 0).setDepth(1);

    // Currency display
    this.palette.workshopCoinIcon = this.add.image(CANVAS_WIDTH - 20, 20, 'icon_coin')
      .setDisplaySize(28, 28).setOrigin(1, 0.5).setDepth(1);
    this.palette.currencyText = this.add.text(CANVAS_WIDTH - 50, 8, '', {
      fontSize: '22px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 0).setDepth(1);
    this.palette.updateCurrencyDisplay();
  }

  /** Get the current available funds */
  private getAvailableFunds(): number {
    if (this.isCampaign) return ProgressionManager.getCurrency();
    return (this.registry.get('gameResources') as number) ?? 0;
  }

  /** Spawn a floating cost animation */
  private spawnCostAnim(amount: number, worldX: number, worldY: number): void {
    if (amount === 0) return;
    const isRefund = amount > 0;
    const label = isRefund ? `+$${amount}` : `-$${Math.abs(amount)}`;
    const color = isRefund ? '#44ff88' : '#ffcc44';

    const txt = this.add.text(worldX, worldY, label, {
      fontSize: '13px', color, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(50).setAlpha(1);

    this.tweens.add({
      targets: txt,
      y: worldY - 30,
      alpha: 0,
      duration: 700,
      ease: 'Power2',
      onComplete: () => txt.destroy(),
    });
  }

  // ══════════════════════════════════════════
  //  DRAG & DROP (coordinates all modules)
  // ══════════════════════════════════════════

  private setupDragDrop(): void {
    const LONG_PRESS_MS = 200;
    const layout = this.gridManager.getLayout();

    // Palette drag start
    this.input.on('dragstart', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Rectangle) => {
      const info = this.placement.getTypeFromPaletteRect(gameObject, this.palette.paletteHighlights);
      if (!info) return;

      this.palette.dragType = info.type;
      this.palette.dragEvoLevel = info.evoLevel;
      this.palette.selectedType = info.type;
      this.palette.selectedEvoLevel = info.evoLevel;
      this.palette.selectPaletteItem(info.type, info.evoLevel);

      const def = COMPONENT_DEFS[info.type];
      const ghost = createComponentVisual(this, info.type, layout.cellSize, def.color, info.evoLevel);
      (ghost as Phaser.GameObjects.Graphics).setDepth(50).setAlpha(0.7);
      this.palette.dragGhost = ghost;
    });

    // Move ghost with pointer
    this.input.on('drag', (pointer: Phaser.Input.Pointer) => {
      if (this.palette.dragGhost) {
        this.palette.dragGhost.setPosition(pointer.x, pointer.y);
      }

      const cell = this.placement.screenToGrid(pointer.x, pointer.y);
      if (cell) {
        this.gridManager.hoverCell = cell;
        const cx = layout.gridX + cell.c * layout.cellSize + layout.cellSize / 2;
        const cy = layout.gridY + cell.r * layout.cellSize + layout.cellSize / 2;
        this.gridManager.hoverRect?.setPosition(cx, cy).setVisible(true);
      } else {
        this.gridManager.hoverCell = null;
        this.gridManager.hoverRect?.setVisible(false);
      }
    });

    // Drop: place on grid (palette drag)
    this.input.on('dragend', (pointer: Phaser.Input.Pointer) => {
      if (!this.palette.dragGhost || !this.palette.dragType) return;

      const cell = this.placement.screenToGrid(pointer.x, pointer.y);
      if (cell) {
        this.placement.placeOnGrid(cell.r, cell.c, this.palette.dragType, 0, false, false, this.palette.dragEvoLevel);
        SoundManager.play('click');
      }

      this.palette.dragGhost.destroy();
      this.palette.dragGhost = null;
      this.palette.dragType = null;
      this.palette.dragEvoLevel = 0;
      this.gridManager.hoverRect?.setVisible(false);
    });

    // Mouse move: grid drag + hover
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.palette.dragGhost) return;

      // Long-press → start grid drag
      if (this.placement.gridDragSource && !this.placement.gridDragActive && pointer.isDown) {
        const elapsed = this.time.now - this.placement.gridDragStartTime;
        if (elapsed >= LONG_PRESS_MS) {
          this.placement.startGridDrag();
        }
      }

      // Move grid drag ghost
      if (this.placement.gridDragGhost) {
        this.placement.gridDragGhost.setPosition(pointer.x, pointer.y);
        const cell = this.placement.screenToGrid(pointer.x, pointer.y);
        if (cell) {
          const cx = layout.gridX + cell.c * layout.cellSize + layout.cellSize / 2;
          const cy = layout.gridY + cell.r * layout.cellSize + layout.cellSize / 2;
          this.gridManager.hoverRect?.setPosition(cx, cy).setVisible(true);
        } else {
          this.gridManager.hoverRect?.setVisible(false);
        }
        return;
      }

      // Normal hover highlight
      const cell = this.placement.screenToGrid(pointer.x, pointer.y);
      if (cell && (this.palette.selectedType || this.gridManager.getCell(cell.r, cell.c) !== null)) {
        const cx = layout.gridX + cell.c * layout.cellSize + layout.cellSize / 2;
        const cy = layout.gridY + cell.r * layout.cellSize + layout.cellSize / 2;
        this.gridManager.hoverRect?.setPosition(cx, cy).setVisible(true);
      } else {
        this.gridManager.hoverRect?.setVisible(false);
      }
    });

    // Pointer up: rotate (quick click) or drop (grid drag)
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) return;

      // Grid drag drop
      if (this.placement.gridDragActive && this.placement.gridDragGhost) {
        const cell = this.placement.screenToGrid(pointer.x, pointer.y);
        let placed = false;
        if (cell && this.placement.gridDragType) {
          this.gridManager.setFlipX(cell.r, cell.c, this.placement.gridDragFlipX);
          this.gridManager.setFlipY(cell.r, cell.c, this.placement.gridDragFlipY);
          placed = this.placement.placeOnGrid(cell.r, cell.c, this.placement.gridDragType, this.placement.gridDragRotation, false, false, this.placement.gridDragEvoLevel);
          if (placed) {
            SoundManager.play('click');
          } else {
            this.gridManager.setFlipX(cell.r, cell.c, false);
            this.gridManager.setFlipY(cell.r, cell.c, false);
          }
        }
        if (!placed && this.placement.gridDragSource && this.placement.gridDragType) {
          this.gridManager.setFlipX(this.placement.gridDragSource.r, this.placement.gridDragSource.c, this.placement.gridDragFlipX);
          this.gridManager.setFlipY(this.placement.gridDragSource.r, this.placement.gridDragSource.c, this.placement.gridDragFlipY);
          this.placement.placeOnGrid(this.placement.gridDragSource.r, this.placement.gridDragSource.c, this.placement.gridDragType, this.placement.gridDragRotation, true, false, this.placement.gridDragEvoLevel);
        }
        this.placement.gridDragGhost.destroy();
        this.placement.gridDragGhost = null;
        this.placement.gridDragActive = false;
        this.placement.gridDragSource = null;
        this.placement.gridDragType = null;
        this.gridManager.hoverRect?.setVisible(false);
        return;
      }

      // Quick click on occupied cell → rotate
      if (this.placement.gridDragSource && !this.placement.gridDragActive) {
        const { r, c } = this.placement.gridDragSource;
        if (this.gridManager.getCell(r, c) !== null) {
          this.placement.rotateOnGrid(r, c);
          SoundManager.play('click');
        }
      }

      this.placement.gridDragSource = null;
      this.placement.gridDragType = null;
      this.placement.gridDragActive = false;
    });

    // ESC to close
    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.palette.upgradeDialog) { this.palette.closeUpgradeDialog(); return; }
      this.close();
    });
    this.input.mouse?.disableContextMenu();
  }

  // ══════════════════════════════════════════
  //  STATS PANEL
  // ══════════════════════════════════════════

  private createStatsPanel(): void {
    const layout = this.gridManager.getLayout();
    const pw = STATS_PANEL_W;
    const ph = layout.topSectionH;
    this.add.rectangle(layout.statsX + pw / 2, layout.statsY + ph / 2, pw, ph, PANEL_BG, 0.85)
      .setStrokeStyle(1, PANEL_BORDER, 0.5).setDepth(1);

    this.nameText = this.add.text(layout.statsX + 10, layout.statsY + 6, this.designName, {
      fontSize: '12px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
      backgroundColor: '#1a1a3a', padding: { x: 6, y: 3 },
    }).setDepth(2).setInteractive({ useHandCursor: true });

    this.nameText.on('pointerdown', () => {
      SoundManager.play('click');
      showTextInputDialog(this, 'Robot name', this.designName, (name) => {
        let finalName = name.substring(0, 20);

        const reaction = GOD.eeNameGame ? checkNameEasterEgg(finalName) : null;
        if (reaction) {
          if (reaction.replaceName) finalName = reaction.replaceName;
          if (reaction.sfx) SoundManager.play(reaction.sfx as any);

          const msgText = this.add.text(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 40, reaction.message, {
            fontSize: '13px', color: '#ffcc44', fontFamily: 'monospace', fontStyle: 'bold',
            stroke: '#000000', strokeThickness: 3, wordWrap: { width: 350 }, align: 'center',
          }).setOrigin(0.5).setDepth(300).setAlpha(0);
          this.tweens.add({
            targets: msgText, alpha: 1, y: msgText.y - 20,
            duration: 300, ease: 'Back.easeOut', yoyo: false,
          });
          this.time.delayedCall(reaction.blockMs || 2500, () => {
            this.tweens.add({
              targets: msgText, alpha: 0, y: msgText.y - 15,
              duration: 400, onComplete: () => msgText.destroy(),
            });
          });
        }

        this.designName = finalName;
        this.nameText.setText(this.designName);
      }, { maxLength: 20 });
    });

    this.add.text(layout.statsX + 10, layout.statsY + 28, t('workshopScene.stats'), {
      fontSize: '12px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setDepth(2);

    const labels = [
      'Cost', 'Mass', 'HP', 'Energy',
      'Force', 'Speed', 'Range',
      'Weapons', 'Support', 'Parts',
      'Stability',
      'Energy Use',
    ];

    const startY = layout.statsY + 50;
    const availableForStats = ph - 60;
    const lineH = Math.min(28, Math.floor(availableForStats / labels.length));

    for (let i = 0; i < labels.length; i++) {
      this.add.text(layout.statsX + 12, startY + i * lineH, labels[i], {
        fontSize: '11px', color: '#666688', fontFamily: 'monospace',
      }).setDepth(2);

      const isEnergyUse = labels[i] === 'Energy Use';
      const val = this.add.text(
        isEnergyUse ? layout.statsX + 12 : layout.statsX + pw - 14,
        startY + i * lineH + (isEnergyUse ? 14 : 0),
        '\u2014',
        {
          fontSize: isEnergyUse ? '8px' : '11px',
          color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
          wordWrap: isEnergyUse ? { width: pw - 24 } : undefined,
        },
      ).setOrigin(isEnergyUse ? 0 : 1, 0).setDepth(2);
      this.statsTexts.push(val);
    }

    this.add.rectangle(layout.statsX + pw / 2, startY + labels.length * lineH + 4, pw - 20, 1, 0x333366, 0.5).setDepth(2);

    this.modFeeText = this.add.text(layout.statsX + 12, startY + labels.length * lineH + 10, '', {
      fontSize: '10px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
      wordWrap: { width: pw - 24 },
    }).setDepth(2).setVisible(false);

    this.warningsText = this.add.text(layout.statsX + 12, startY + labels.length * lineH + 28, '', {
      fontSize: '9px', color: '#ff6644', fontFamily: 'monospace',
      wordWrap: { width: pw - 24 }, lineSpacing: 3,
    }).setDepth(2);
  }

  // ══════════════════════════════════════════
  //  ACTION BUTTONS
  // ══════════════════════════════════════════

  /** Returns the current template list (live JSON-loaded or hardcoded fallback). */
  public static getTemplateList(): readonly RobotTemplate[] { return getTemplates(); }

  private loadTemplate(index: number): void {
    const tmpl = getTemplates()[index];
    if (!tmpl) return;
    // Batch mode: skip per-cell recalcStats/drawConnections/refreshRuleDisplays
    this.gridManager.batchMode = true;
    this.gridManager.clearGrid({
      recalcStats: () => {},
      refreshRuleDisplays: () => {},
    });
    this.gridManager.loadComponentsToGrid(
      tmpl.components, false,
      (r, c, type, rot, skipV, skipC, evo) => this.placement.placeOnGrid(r, c, type, rot, skipV, skipC, evo),
      () => {},
    );
    this.gridManager.batchMode = false;
    // Single recalc/draw at the end
    this.gridManager.drawConnections();
    this.recalcStats();
    this.rulesEditor.rules = tmpl.rules.map(r => ({ ...r }));
    this.rulesEditor.refreshRuleDisplays();
    this.designName = tmpl.label || tmpl.name;
    if (this.nameText) this.nameText.setText(this.designName);
  }

  private createButtons(): void {
    const layout = this.gridManager.getLayout();
    const barY = layout.paletteY - 56;

    this.add.rectangle(CANVAS_WIDTH / 2, barY + 24, CANVAS_WIDTH, 56, 0x0a0a18, 0.95).setDepth(12);

    const leftX = MARGIN + 4;
    const clearBtn = createSmallButton(this, leftX + 30, barY + 26, 'Clear', TECH.DIM, () => {
      SoundManager.play('clickCancel');
      this.gridManager.batchMode = true;
      this.gridManager.clearGrid({ recalcStats: () => {}, refreshRuleDisplays: () => {} });
      this.gridManager.batchMode = false;
      this.gridManager.drawConnections();
      this.recalcStats();
      this.rulesEditor.refreshRuleDisplays();
    });
    clearBtn.setDepth(13);

    const rotAllBtn = createSmallButton(this, leftX + 110, barY + 26, '\u21BB 90\u00B0', 0x88aaff, () => {
      SoundManager.play('click');
      this.gridManager.batchMode = true;
      this.gridManager.rotateAll90(
        (r, c, type, rot, skipV, skipC, evo) => this.placement.placeOnGrid(r, c, type, rot, skipV, skipC, evo),
        { recalcStats: () => {}, refreshRuleDisplays: () => {} },
      );
      this.gridManager.batchMode = false;
      this.gridManager.drawConnections();
      this.recalcStats();
      this.rulesEditor.refreshRuleDisplays();
    });
    rotAllBtn.setDepth(13);

    this.placement.flipToggleBtn = createSmallButton(this, leftX + 200, barY + 26, '\u21D4 Flip', 0x88ccff, () => {
      this.placement.flipModeActive = !this.placement.flipModeActive;
      this.placement.flipToggleBtn!.setAlpha(this.placement.flipModeActive ? 1.0 : 0.45);
      SoundManager.play('click');
    });
    this.placement.flipToggleBtn.setDepth(13).setAlpha(0.45);

    const buildBtn = createButton(this, CANVAS_WIDTH - MARGIN - 46, barY + 26, 'Save', {
      onClick: () => { SoundManager.play('clickConfirm'); this.finishDesign(); },
      color: TECH.SUCCESS, fontSize: '12px', height: 30,
      depth: 13,
    });

    const cancelBtn = createButton(this, CANVAS_WIDTH - MARGIN - 158, barY + 26, 'Cancel', {
      onClick: () => { SoundManager.play('clickCancel'); this.close(); },
      color: TECH.DANGER, fontSize: '12px', height: 30,
      depth: 13,
    });

    // Dev-only: CINE editor button
    const cineBtn = createSmallTechButton(this, leftX + 290, barY + 26, 'CINE', 0x44aaff, () => {
      SoundManager.play('click');
      if (this.scene.isActive('CinematicEditorScene')) return;
      const callerScenes = [this.scene.key];
      for (const key of callerScenes) this.scene.pause(key);
      const initData: Record<string, unknown> = { hostSceneKey: this.scene.key, callerScenes };
      const ids = getSequenceIds();
      if (ids.length > 0) initData.sequence = getSequence(ids[0]);
      this.scene.launch('CinematicEditorScene', initData);
    });
    cineBtn.setDepth(13).setVisible(isDevMode());
    EventBus.on(DEV_MODE_CHANGED, (on: boolean) => { cineBtn.setVisible(on); });
    this.events.once('shutdown', () => { EventBus.off(DEV_MODE_CHANGED); });

    // Templates
    const { cols, rows } = this.gridManager.getDimensions();
    const halfCols = Math.floor(cols / 2);
    const halfRows = Math.floor(rows / 2);
    const allTemplates = getTemplates();
    const visibleTemplates: { index: number; tmpl: RobotTemplate }[] = [];
    for (let i = 0; i < allTemplates.length; i++) {
      const tmpl = allTemplates[i];
      const allUnlocked = tmpl.components.every(c => ProgressionManager.isComponentUnlocked(c.type));
      if (!allUnlocked) continue;
      const fitsGrid = tmpl.components.every(c => {
        const gc = Math.round(c.lx / COMPONENT_SPACING);
        const gr = Math.round(c.ly / COMPONENT_SPACING);
        const col = gc + halfCols;
        const row = gr + halfRows;
        return col >= 0 && col < cols && row >= 0 && row < rows;
      });
      if (!fitsGrid) continue;
      visibleTemplates.push({ index: i, tmpl });
    }

    if (visibleTemplates.length > 0) {
      const TMPL_COLS = 7;
      const TMPL_ROWS = Math.ceil(visibleTemplates.length / TMPL_COLS);
      const btnEstW = 90;
      const btnGap = 8;
      const rowH = 34;
      const totalW = TMPL_COLS * btnEstW + (TMPL_COLS - 1) * btnGap;
      const gridCenterX = layout.gridX + layout.gridW / 2;
      const startX = gridCenterX - totalW / 2;
      const stripH = TMPL_ROWS * rowH + 28;
      this.add.rectangle(CANVAS_WIDTH / 2, barY - stripH / 2 + 10, CANVAS_WIDTH, stripH, 0x0a0a18, 0.95).setDepth(12);

      for (let i = 0; i < visibleTemplates.length; i++) {
        const vt = visibleTemplates[i];
        const col = i % TMPL_COLS;
        const row = Math.floor(i / TMPL_COLS);
        const bx = startX + col * (btnEstW + btnGap) + btnEstW / 2;
        const by = barY - 4 - (TMPL_ROWS - 1 - row) * rowH;

        const tmplColor = parseInt(vt.tmpl.color.replace('#', ''), 16) || TECH.COLOR;
        const btn = createSmallButton(this, bx, by, vt.tmpl.label || vt.tmpl.name, tmplColor, () => {
          SoundManager.play('clickConfirm'); this.loadTemplate(vt.index);
        });
        btn.setDepth(13);
      }
    }
  }

  // ══════════════════════════════════════════
  //  TOOLTIP
  // ══════════════════════════════════════════

  private createTooltip(): void {
    this.tooltip = this.add.container(0, 0).setDepth(100).setVisible(false);

    this.tooltipBg = this.add.rectangle(0, 0, 200, 80, 0x0a0a1e, 0.96)
      .setStrokeStyle(1, 0x4444aa).setOrigin(0, 0);
    this.tooltip.add(this.tooltipBg);

    this.tooltipTitle = this.add.text(8, 6, '', {
      fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      wordWrap: { width: 280 },
    });
    this.tooltip.add(this.tooltipTitle);

    this.tooltipStats = this.add.text(8, 22, '', {
      fontSize: '8px', color: '#aaaacc', fontFamily: 'monospace',
    });
    this.tooltip.add(this.tooltipStats);

    this.tooltipDesc = this.add.text(8, 40, '', {
      fontSize: '8px', color: '#888899', fontFamily: 'monospace',
      wordWrap: { width: 280 }, lineSpacing: 2,
    });
    this.tooltip.add(this.tooltipDesc);
  }

  private showTooltip(x: number, y: number, def: ComponentDef, locked = false, evoLevel = 0): void {
    // Use evolved stats for display
    const eDef = evoLevel > 0 ? evolveComponentDef(def, evoLevel) : def;
    const tipCost = locked
      ? Math.round(def.unlockCost * GOD.workshopPriceMult)
      : GOD.freeBuilding ? 0 : Math.round(def.costPerLevel[Math.min(evoLevel, 2)] * GOD.componentCostMult);
    const compName = t(`component.${def.type}.name`);
    const lvlSuffix = evoLevel > 0 ? ` Lv.${evoLevel + 1}` : '';
    this.tooltipTitle.setText(locked ? `[LOCKED] ${compName}  (Unlock: $${tipCost})` : `${compName}${lvlSuffix}  $${tipCost}`);

    const stats: string[] = [];
    if (eDef.hp > 0) stats.push(`HP:${eDef.hp}`);
    if (eDef.mass > 0) stats.push(`M:${eDef.mass}`);
    if (eDef.energy > 0) stats.push(`E+${eDef.energy}`);
    if (eDef.energy < 0) stats.push(`E${eDef.energy}/s`);
    if (eDef.force > 0) stats.push(`F:${eDef.force}`);
    if (eDef.sensorRange > 0) stats.push(`R:${(eDef.sensorRange / (TILE_SIZE * Math.SQRT2)).toFixed(1)}D`);
    if (eDef.damage > 0) stats.push(`D:${eDef.damage}`);
    if (eDef.fireRate > 0) stats.push(`Cd:${(1 / eDef.fireRate).toFixed(1)}s`);
    if (eDef.repairRate > 0) stats.push(`Rp:${eDef.repairRate}/s`);
    if (eDef.rechargeRate > 0) stats.push(`Re:${eDef.rechargeRate}/s`);
    if (eDef.explosionDamage > 0) stats.push(`Exp:${eDef.explosionDamage}`);
    if (eDef.projectileSpeed > 0) stats.push(`Spd:${eDef.projectileSpeed}`);

    this.tooltipStats.setText(stats.join(' \u00B7 '));

    // Build description: base desc + evolution improvements
    let desc = t(`component.${def.type}.desc`) || '';
    if (evoLevel > 0) {
      const diffs: string[] = [];
      if (eDef.damage > def.damage) diffs.push(`DMG ×${(eDef.damage / def.damage).toFixed(2)}`);
      if (eDef.weaponRange > def.weaponRange) diffs.push(`RNG ×${(eDef.weaponRange / def.weaponRange).toFixed(2)}`);
      if (eDef.hp !== def.hp) diffs.push(`HP ${eDef.hp}`);
      if (eDef.sensorRange > def.sensorRange) diffs.push(`Sensor ×${(eDef.sensorRange / def.sensorRange).toFixed(2)}`);
      if (eDef.projectileSpeed > def.projectileSpeed) diffs.push(`Speed ×${(eDef.projectileSpeed / def.projectileSpeed).toFixed(2)}`);
      if (eDef.repairRate > def.repairRate) diffs.push(`Repair ×${(eDef.repairRate / def.repairRate).toFixed(2)}`);
      if (eDef.rechargeRate > def.rechargeRate) diffs.push(`Recharge ×${(eDef.rechargeRate / def.rechargeRate).toFixed(2)}`);
      if (diffs.length > 0) desc += `\n[+] ${diffs.join(', ')}`;
    }
    this.tooltipDesc.setText(desc);

    const pad = 8;
    const contentW = Math.max(
      this.tooltipTitle.width,
      this.tooltipStats.width,
      this.tooltipDesc.width,
    ) + pad * 2;
    const contentH = this.tooltipDesc.y + this.tooltipDesc.height + pad;
    const tipW = Math.max(200, contentW);
    const tipH = Math.max(80, contentH);
    this.tooltipBg.setSize(tipW, tipH);

    const tx = Math.min(x, CANVAS_WIDTH - tipW - 10);
    const ty = Math.min(y, CANVAS_HEIGHT - tipH - 10);
    this.tooltip.setPosition(tx, ty).setVisible(true);
  }

  private hideTooltip(): void {
    this.tooltip.setVisible(false);
  }

  // ── Challenge mode intro dialog (shown once) ──

  private showChallengeIntro(): void {
    const container = this.add.container(0, 0).setDepth(300);

    // Dim overlay
    const overlay = this.add.rectangle(
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2,
      CANVAS_WIDTH, CANVAS_HEIGHT,
      0x000000, 0.65,
    ).setInteractive();
    container.add(overlay);

    const pw = 380;
    const ph = 260;
    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;

    // Panel background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.97);
    bg.fillRoundedRect(cx - pw / 2, cy - ph / 2, pw, ph, 8);
    drawTechRect(bg, cx - pw / 2, cy - ph / 2, pw, ph, 0xffcc00, 0.7, 1.5);
    container.add(bg);

    // Blocker to prevent clicks through panel
    const blocker = this.add.rectangle(cx, cy, pw, ph, 0x000000, 0).setInteractive();
    container.add(blocker);

    // Title
    const title = this.add.text(cx, cy - ph / 2 + 22, t('challenge.introTitle'), {
      fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(title);

    // Differences list
    const lines = [
      t('challenge.introLine1'),
      t('challenge.introLine2'),
      t('challenge.introLine3'),
      t('challenge.introLine4'),
      t('challenge.introLine5'),
    ];
    const body = this.add.text(cx - pw / 2 + 24, cy - ph / 2 + 50, lines.join('\n'), {
      fontSize: '11px', color: '#ccddee', fontFamily: 'monospace',
      lineSpacing: 6, wordWrap: { width: pw - 48 },
    });
    container.add(body);

    // OK button
    const dismiss = () => {
      localStorage.setItem('rfd_challenge_intro_seen', '1');
      container.destroy();
    };
    const okBtn = createButton(this, cx, cy + ph / 2 - 30, t('challenge.introOk'), {
      onClick: () => { SoundManager.play('clickConfirm'); dismiss(); },
      width: 140, height: 32, fontSize: '13px', color: 0xffcc00,
    });
    container.add(okBtn);

    // Also dismiss on overlay click
    overlay.on('pointerdown', () => { SoundManager.play('clickCancel'); dismiss(); });
  }

  /** Show a floating bubble hinting the player to unlock in campaign mode */
  private showCampaignHint(localX: number, localY: number): void {
    if (this.campaignHint) { this.campaignHint.destroy(); this.campaignHint = undefined; }

    const screenX = this.palette.paletteContainer.x + localX;
    const screenY = localY - 10;

    const container = this.add.container(screenX, screenY).setDepth(100);

    const msg = this.add.text(0, 0, 'Unlock in Campaign mode', {
      fontSize: '11px', color: '#ffcc44', fontFamily: 'monospace', fontStyle: 'bold',
      backgroundColor: '#1a1a2e', padding: { x: 8, y: 5 },
    }).setOrigin(0.5, 1);

    const bw = msg.width + 2;
    const bh = msg.height + 2;
    const frame = this.add.graphics();
    frame.lineStyle(1, 0xffcc44, 0.6);
    frame.strokeRect(-bw / 2, -msg.height - 1, bw, bh);

    container.add([frame, msg]);
    this.campaignHint = container;

    this.tweens.add({
      targets: container,
      y: screenY - 30,
      alpha: 0,
      duration: 1500,
      ease: 'Power1',
      onComplete: () => {
        container.destroy();
        if (this.campaignHint === container) this.campaignHint = undefined;
      },
    });
  }

  // ══════════════════════════════════════════
  //  STATS CALCULATION
  // ══════════════════════════════════════════

  /** Get current grid components for validation */
  private getGridTypes(): ComponentType[] {
    const { rows, cols } = this.gridManager.getDimensions();
    return getGridComponentTypes(this.gridManager.getGridState(), rows, cols);
  }

  private recalcStats(): void {
    const { rows, cols } = this.gridManager.getDimensions();
    const layout = this.gridManager.getLayout();
    let cost = 0, mass = 0, hp = 0, energy = 0;
    let force = 0, sensorRange = 0;
    let weaponCount = 0, supportCount = 0, partCount = 0;
    const sensorRanges: number[] = [];

    for (let ri = 0; ri < rows; ri++) {
      for (let ci = 0; ci < cols; ci++) {
        const cell = this.gridManager.getCell(ri, ci);
        if (cell === null) continue;
        const def = COMPONENT_DEFS[cell];
        partCount++;
        const cLvl = this.gridManager.getEvoLevel(ri, ci);
        cost += def.costPerLevel[Math.min(cLvl, 2)];
        mass += Math.round(def.mass * (1 + GOD.tierMassMult * cLvl));
        hp += def.hp;
        if (def.energy > 0) energy += def.energy;
        if (def.category === ComponentCategory.MOVEMENT) force += def.force;
        if (def.category === ComponentCategory.SENSOR) sensorRanges.push(def.sensorRange);
        if (def.category === ComponentCategory.WEAPON) weaponCount++;
        if (def.category === ComponentCategory.SUPPORT) supportCount++;
      }
    }

    sensorRanges.sort((a, b) => b - a);
    for (let i = 0; i < sensorRanges.length; i++) {
      sensorRange += i === 0 ? sensorRanges[i] : sensorRanges[i] * 0.5;
    }
    sensorRange = Math.round(sensorRange);

    const isSupport = supportCount > 0;
    if (isSupport) hp = Math.round(hp * 0.5);

    this.totalCost = cost;
    const speedPx = mass > 0 ? (force / mass) * SPEED_REFERENCE_MASS : 0;
    const speedTiles = speedPx / TILE_SIZE;

    let hasBattery = false;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.gridManager.getCell(r, c) === ComponentType.BATTERY) hasBattery = true;
      }
    }

    const st = this.statsTexts;
    const effectCost = GOD.freeBuilding ? 0 : Math.round(cost * GOD.componentCostMult);
    // Always show assembly cost normally
    st[0]?.setText(`$${effectCost}`).setColor(cost > 0 ? '#ffcc00' : '#aaaaaa');
    // In edit mode: show fee only if design actually changed (rename is free)
    if (this.editingRobotId && this.modFee > 0 && this.hasDesignChanged()) {
      const rawDiff = cost - this.originalCost;
      const totalDue = Math.max(0, rawDiff) + this.modFee;
      const canAfford = this.playerResources >= totalDue;
      const parts: string[] = [];
      if (rawDiff > 0) parts.push(`upgrade +$${rawDiff}`);
      parts.push(`mod fee $${this.modFee}`);
      this.modFeeText?.setText(`${parts.join(' + ')} = $${totalDue}`)
        .setColor(canAfford ? '#ffaa44' : '#ff4444')
        .setVisible(true);
    } else {
      this.modFeeText?.setVisible(false);
    }
    st[1]?.setText(`${mass}`);
    st[2]?.setText(isSupport ? `${hp} (\u00F72)` : `${hp}`).setColor(isSupport ? '#ff9944' : '#ffffff');
    st[3]?.setText(`${energy}`).setColor(energy === 0 && partCount > 0 ? '#ff6644' : '#ffffff');
    st[4]?.setText(`${force}`);
    st[5]?.setText(`${speedTiles.toFixed(1)} c/s`);
    const sensorDiag = (sensorRange / (TILE_SIZE * Math.SQRT2)).toFixed(1);
    st[6]?.setText(`${sensorDiag}D`).setColor(sensorRange === 0 && partCount > 0 ? '#ff6644' : '#ffffff');
    st[7]?.setText(isSupport && weaponCount > 0 ? `${weaponCount} (\u00D70.5)` : `${weaponCount}`)
      .setColor(isSupport && weaponCount > 0 ? '#ff9944' : weaponCount === 0 && partCount > 0 ? '#ff6644' : '#ffffff');
    st[8]?.setText(isSupport ? `${supportCount} \u26A1\u00D72` : `${supportCount}`)
      .setColor(isSupport ? '#66ffaa' : '#ffffff');
    st[9]?.setText(`${partCount}`);

    // Stability
    const centerCol = Math.floor(cols / 2);
    const centerRow = Math.floor(rows / 2);
    const compEntries: { type: ComponentType; lx: number; ly: number }[] = [];
    let dirHeadingForStab: number | undefined;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const ct = this.gridManager.getCell(r, c);
        if (ct === null) continue;
        compEntries.push({ type: ct, lx: (c - centerCol) * COMPONENT_SPACING, ly: (r - centerRow) * COMPONENT_SPACING });
        if (ct === ComponentType.DIRECTION) {
          dirHeadingForStab = this.gridManager.getRotation(r, c);
        }
      }
    }
    const stab = calculateStability(compEntries, false, dirHeadingForStab);
    const stabPct = Math.round(stab.score * 100);
    const stabColor = stabPct >= 80 ? '#44ff44' : stabPct >= 50 ? '#ffcc00' : '#ff4444';
    let stabText = `${stabPct}% ${stab.label}`;
    if (stab.foundationFactor < 0.95 && stab.foundationPenaltyTypes.length > 0) {
      stabText += ' ⚠ base';
    }
    st[10]?.setText(stabText).setColor(stabColor);

    const adjSpeed = (speedPx * stabilitySpeedMultiplier(stab.score)) / TILE_SIZE;
    st[5]?.setText(`${adjSpeed.toFixed(1)} c/s`);

    // ── Energy Use: per-action cost breakdown ──
    const energyCosts: string[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.gridManager.getCell(r, c);
        if (cell === null) continue;
        const def = COMPONENT_DEFS[cell];
        const drain = Math.max(0, -def.energy);
        if (drain === 0) continue;
        const shortName = def.name.split(' ')[0].substring(0, 5);
        if (def.type === ComponentType.RECHARGE_MODULE) {
          energyCosts.push(`Rech:+${def.rechargeRate}/s`);
        } else if (def.category === ComponentCategory.WEAPON) {
          if (def.type === ComponentType.FLAMETHROWER) {
            energyCosts.push(`${shortName}:${drain}/s`);
          } else if (def.fireRate > 0) {
            energyCosts.push(`${shortName}:${Math.round(drain / def.fireRate)}/sh`);
          }
        } else if (def.type === ComponentType.MINE_LAYER) {
          energyCosts.push(`Mine:${MINE_CONFIG.energyPerMine}/m`);
        } else if (def.type === ComponentType.REPAIR_MODULE) {
          energyCosts.push(`Repai:${drain}/s`);
        } else if (def.type === ComponentType.EXCAVATOR) {
          energyCosts.push(`Excav:${EXCAVATOR_CONFIG.energyPerHarvest}/s`);
        } else if (def.type === ComponentType.CAMOUFLAGE) {
          energyCosts.push(`Camo:${drain}/s`);
        }
      }
    }
    if (energyCosts.length > 0) {
      st[11]?.setText(energyCosts.join(' ')).setColor('#ff9966');
    } else {
      st[11]?.setText(energy > 0 ? 'none' : '\u2014').setColor('#666688');
    }

    // Center of Mass indicator
    if (compEntries.length > 0) {
      const comScreenX = layout.gridX + (centerCol + stab.comX / COMPONENT_SPACING) * layout.cellSize + layout.cellSize / 2;
      const comScreenY = layout.gridY + (centerRow + stab.comY / COMPONENT_SPACING) * layout.cellSize + layout.cellSize / 2;

      this.gridManager.comMarker.clear();
      this.gridManager.comMarker.lineStyle(2, 0xff4466, 0.9);
      this.gridManager.comMarker.strokeCircle(comScreenX, comScreenY, 6);
      this.gridManager.comMarker.lineStyle(1, 0xff4466, 0.7);
      this.gridManager.comMarker.lineBetween(comScreenX - 10, comScreenY, comScreenX + 10, comScreenY);
      this.gridManager.comMarker.lineBetween(comScreenX, comScreenY - 10, comScreenX, comScreenY + 10);
      this.gridManager.comMarker.fillStyle(0xff4466, 1);
      this.gridManager.comMarker.fillCircle(comScreenX, comScreenY, 2);
      this.gridManager.comMarker.setVisible(true);
      this.gridManager.comLabel.setPosition(comScreenX, comScreenY).setVisible(true);
    } else {
      this.gridManager.comMarker.clear();
      this.gridManager.comMarker.setVisible(false);
      this.gridManager.comLabel.setVisible(false);
    }

    // Sensor range overlay
    this.gridManager.sensorOverlay.clear();
    this.gridManager.sensorRangeLabel.setVisible(false);
    if (sensorRange > 0) {
      const cx = layout.gridX + centerCol * layout.cellSize + layout.cellSize / 2;
      const cy = layout.gridY + centerRow * layout.cellSize + layout.cellSize / 2;

      const scaleFactor = layout.cellSize / COMPONENT_SPACING;
      const rawRadius = sensorRange * scaleFactor * 0.4;
      const maxRadius = Math.max(layout.gridW, layout.gridH) * 0.6;
      const displayRadius = Math.min(rawRadius, maxRadius);
      const isCapped = rawRadius > maxRadius;

      let hasDirection = false;
      let dirHeading = Math.PI;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (this.gridManager.getCell(r, c) === ComponentType.DIRECTION) {
            hasDirection = true;
            dirHeading = this.gridManager.getRotation(r, c);
            break;
          }
        }
        if (hasDirection) break;
      }

      if (hasDirection) {
        const halfAngle = Math.PI / 6;
        this.gridManager.sensorOverlay.fillStyle(0x44cc44, 0.08);
        this.gridManager.sensorOverlay.beginPath();
        this.gridManager.sensorOverlay.moveTo(cx, cy);
        this.gridManager.sensorOverlay.arc(cx, cy, displayRadius,
          dirHeading - halfAngle, dirHeading + halfAngle, false);
        this.gridManager.sensorOverlay.closePath();
        this.gridManager.sensorOverlay.fillPath();
        this.gridManager.sensorOverlay.lineStyle(2, 0x44cc44, 0.4);
        this.gridManager.sensorOverlay.beginPath();
        this.gridManager.sensorOverlay.arc(cx, cy, displayRadius,
          dirHeading - halfAngle, dirHeading + halfAngle, false);
        this.gridManager.sensorOverlay.strokePath();
      } else {
        this.gridManager.sensorOverlay.fillStyle(0x44cc44, 0.06);
        this.gridManager.sensorOverlay.lineStyle(2, 0x44cc44, 0.3);
        this.gridManager.sensorOverlay.fillCircle(cx, cy, displayRadius);
        this.gridManager.sensorOverlay.strokeCircle(cx, cy, displayRadius);
      }

      if (isCapped) {
        this.gridManager.sensorOverlay.lineStyle(1, 0x44cc44, 0.15);
        this.gridManager.sensorOverlay.strokeCircle(cx, cy, rawRadius);
      }

      const labelY = Math.max(layout.gridY + 4, cy - displayRadius + 8);
      this.gridManager.sensorRangeLabel.setPosition(cx, labelY);
      const rangeDiag = (sensorRange / (TILE_SIZE * Math.SQRT2)).toFixed(1);
      this.gridManager.sensorRangeLabel.setText(`Range: ${rangeDiag}D`);
      this.gridManager.sensorRangeLabel.setVisible(true);
    }

    // Validation warnings
    let hasWing = false, hasWheel = false, hasThruster = false;
    let hasAirWeapon = false, hasGroundOnlyWeapon = false, hasBothWeapon = false;
    let lowestOccupiedRow = -1;
    let lowestWheelRow = -1;
    let highestWheelRow = rows;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const ct = this.gridManager.getCell(r, c);
        if (ct === null) continue;
        lowestOccupiedRow = Math.max(lowestOccupiedRow, r);
        if (ct === ComponentType.WING) hasWing = true;
        if (ct === ComponentType.WHEEL) {
          hasWheel = true;
          highestWheelRow = Math.min(highestWheelRow, r);
          lowestWheelRow = Math.max(lowestWheelRow, r);
        }
        if (ct === ComponentType.THRUSTER) hasThruster = true;
        const def = COMPONENT_DEFS[ct];
        if (def.category === ComponentCategory.WEAPON) {
          if (def.weaponTarget === WeaponTarget.AIR) hasAirWeapon = true;
          if (def.weaponTarget === WeaponTarget.GROUND) hasGroundOnlyWeapon = true;
          if (def.weaponTarget === WeaponTarget.BOTH) hasBothWeapon = true;
        }
      }
    }

    const wheelsNotLowest = hasWheel && lowestWheelRow >= 0 && lowestWheelRow < lowestOccupiedRow;
    const { connected: connectedCells, occupied } = this.gridManager.getConnectedCells();
    const connected = connectedCells.size === occupied.length;

    this.gridManager.updateIsolatedOverlays(connectedCells, occupied);

    let hasDirectionComp = false;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.gridManager.getCell(r, c) === ComponentType.DIRECTION) { hasDirectionComp = true; break; }
      }
      if (hasDirectionComp) break;
    }
    // Bottom jumpers = hop locomotion (mobile). Check if any jumper sits in the lowest rows.
    let hasBottomJumper = false;
    const jumperRows: number[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.gridManager.getCell(r, c) === ComponentType.JUMPER) jumperRows.push(r);
      }
    }
    if (jumperRows.length > 0) {
      hasBottomJumper = jumperRows.some(r => r >= lowestOccupiedRow - 1);
    }
    const isMobile = hasWheel || hasWing || hasThruster || hasBottomJumper;

    const maxColHeight = GOD.eeStackOverflow ? getMaxColumnHeight(this.gridManager.getGridState() as (string | null)[][]) : 0;
    const stackWarning = GOD.eeStackOverflow ? getStackOverflowWarning(maxColHeight) : null;

    const warnings: string[] = [];
    if (partCount > 0) {
      if (!connected) warnings.push(`\u26A0 ${t('workshopScene.disconnected')}`);
      if (isMobile && !hasDirectionComp) warnings.push('\u26A0 No direction arrow!');
      if (sensorRange === 0) warnings.push('\u26A0 No sensor');
      if (weaponCount === 0) warnings.push('\u26A0 No weapon');
      if (!hasBattery) warnings.push('\u26A0 No battery');
      if (wheelsNotLowest) warnings.push('\u26A0 Wheels must be at bottom');
      if (hasWing && !hasThruster) warnings.push('\u26A0 Wings without thruster');
      if (hasAirWeapon && !hasBothWeapon) warnings.push('\u2139 Air weapon only \u2014 add ground or dual');
      if (hasGroundOnlyWeapon && !hasAirWeapon && !hasBothWeapon) warnings.push('\u2139 Ground weapon only \u2014 can\'t hit flyers');
      if (stackWarning) warnings.push(`\u26A0 ${stackWarning}`);
    }
    this.warningsText?.setText(warnings.join('\n'));
  }

  // ══════════════════════════════════════════
  //  DEV TEMPLATE EDITOR
  // ══════════════════════════════════════════

  private createTemplateEditor(): void {
    this.templateEditor = new TemplateEditorPanel(this, {
      loadTemplate: (index: number) => this.loadTemplate(index),
      snapshotForTemplate: () => this.snapshotForTemplate(),
      getDesignName: () => this.designName,
    });
  }

  /** Snapshot current grid as template-compatible data (lx/ly centered, with evoLevel/flip). */
  private snapshotForTemplate(): { components: RobotTemplate['components']; rules: BehaviorRule[] } {
    const { rows, cols } = this.gridManager.getDimensions();
    const centerCol = Math.floor(cols / 2);
    const centerRow = Math.floor(rows / 2);
    const components: RobotTemplate['components'] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const ct = this.gridManager.getCell(r, c);
        if (ct === null) continue;
        const lx = (c - centerCol) * COMPONENT_SPACING;
        const ly = (r - centerRow) * COMPONENT_SPACING;
        const rotation = this.gridManager.getRotation(r, c);
        const flipX = this.gridManager.getFlipX(r, c);
        const flipY = this.gridManager.getFlipY(r, c);
        const evoLevel = this.gridManager.getEvoLevel(r, c);
        components.push({
          type: ct, lx, ly,
          rotation: rotation || undefined,
          flipX: flipX || undefined,
          flipY: flipY || undefined,
          evoLevel: evoLevel || undefined,
        });
      }
    }
    return { components, rules: this.rulesEditor.rules.map(r => ({ ...r })) };
  }

  // ══════════════════════════════════════════
  //  BUILD OUTPUT
  // ══════════════════════════════════════════

  private finishDesign(): void {
    const { rows, cols } = this.gridManager.getDimensions();
    const components: RobotDesign['components'] = [];
    const centerCol = Math.floor(cols / 2);
    const centerRow = Math.floor(rows / 2);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const ct = this.gridManager.getCell(r, c);
        if (ct === null) continue;
        const lx = (c - centerCol) * COMPONENT_SPACING;
        const ly = (r - centerRow) * COMPONENT_SPACING;
        const rotation = this.gridManager.getRotation(r, c);
        components.push({
          type: ct, lx, ly, rotation,
          flipX: this.gridManager.getFlipX(r, c) || undefined,
          flipY: this.gridManager.getFlipY(r, c) || undefined,
          evoLevel: this.gridManager.getEvoLevel(r, c) || undefined,
        });
      }
    }

    if (components.length === 0) return;

    if (components.length > 1 && !this.gridManager.checkConnectivity()) {
      this.gridManager.flashIsolatedComponents();
      return;
    }

    // In edit mode: check affordability before saving (rename is free, only charge if design changed)
    if (this.editingRobotId && this.modFee > 0 && this.hasDesignChanged()) {
      const rawDiff = this.totalCost - this.originalCost;
      const totalDue = Math.max(0, rawDiff) + this.modFee;
      if (this.playerResources < totalDue) {
        // Flash cost text red to indicate can't afford
        const st0 = this.statsTexts[0];
        if (st0) {
          st0.setColor('#ff0000');
          this.tweens.add({ targets: st0, alpha: 0.3, yoyo: true, repeat: 2, duration: 150,
            onComplete: () => st0.setAlpha(1) });
        }
        SoundManager.play('clickCancel');
        return;
      }
    }

    // Keep user-edited rules — only generate defaults if none exist
    const currentRules = this.rulesEditor.rules;
    if (!currentRules.length) {
      const gridTypes = components.map(c => c.type);
      const defaults = generateDefaultRules(gridTypes);
      for (const r of defaults) currentRules.push(r);
      this.rulesEditor.refreshRuleDisplays();
    }

    const design: RobotDesign = {
      components,
      rules: currentRules.map(r => ({ ...r })),
      cost: this.totalCost,
    };

    if (this.editingRobotId) {
      EventBus.emit(GameEvents.WORKSHOP_EDIT_COMPLETE, {
        design,
        robotId: this.editingRobotId,
        name: this.designName,
      });
    } else {
      DesignStore.add(this.designName, design);
      EventBus.emit(GameEvents.WORKSHOP_DESIGN_COMPLETE, { design, name: this.designName });
    }

    const colHeight = GOD.eeStackOverflow ? getMaxColumnHeight(this.gridManager.getGridState() as (string | null)[][]) : 0;
    if (GOD.eeStackOverflow && colHeight >= 8) {
      incrementEasterEggStat('jengaBuilds');
    }

    this.close();
  }

  /** Check if current grid differs from original components (for mod fee). */
  private hasDesignChanged(): boolean {
    // Extract current components from grid (same logic as finishDesign)
    const { rows, cols } = this.gridManager.getDimensions();
    const centerCol = Math.floor(cols / 2);
    const centerRow = Math.floor(rows / 2);
    const current: WorkshopEditData['components'] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const ct = this.gridManager.getCell(r, c);
        if (ct === null) continue;
        current.push({
          type: ct,
          lx: (c - centerCol) * COMPONENT_SPACING,
          ly: (r - centerRow) * COMPONENT_SPACING,
          rotation: this.gridManager.getRotation(r, c),
          flipX: this.gridManager.getFlipX(r, c) || undefined,
          flipY: this.gridManager.getFlipY(r, c) || undefined,
          evoLevel: this.gridManager.getEvoLevel(r, c) || undefined,
        });
      }
    }
    if (current.length !== this.originalComponents.length) return true;
    const sort = (a: { type: string; lx: number; ly: number }, b: { type: string; lx: number; ly: number }) =>
      a.lx - b.lx || a.ly - b.ly;
    const sortedOld = [...this.originalComponents].sort(sort);
    const sortedNew = [...current].sort(sort);
    for (let i = 0; i < sortedOld.length; i++) {
      const o = sortedOld[i];
      const n = sortedNew[i];
      if (o.type !== n.type || o.lx !== n.lx || o.ly !== n.ly
        || (o.rotation ?? 0) !== (n.rotation ?? 0)
        || (o.evoLevel ?? 0) !== (n.evoLevel ?? 0)
        || !!(o.flipX) !== !!(n.flipX)
        || !!(o.flipY) !== !!(n.flipY)) {
        return true;
      }
    }
    return false;
  }

  private close(): void {
    this.scene.stop('WorkshopScene');
    this.scene.resume('GameScene');
    this.scene.resume('UIScene');
    const node = this.registry.get('currentLevelNode') as { index?: number } | undefined;
    if (node?.index != null) {
      SoundManager.playLevelMusicForMap(node.index);
    } else {
      SoundManager.playRandomLevelMusic();
    }
  }

  /** Returns screen-space rects for tutorial highlight targets */
  getTutorialTargetRect(key: string): { x: number; y: number; w: number; h: number } {
    const layout = this.gridManager.getLayout();
    switch (key) {
      case 'grid':
        return { x: layout.gridX, y: layout.gridY, w: layout.gridW, h: layout.gridH };
      case 'palette':
        return { x: 10, y: layout.paletteY, w: CANVAS_WIDTH - 20, h: 130 };
      case 'stats':
        return { x: layout.statsX, y: TITLE_H, w: STATS_PANEL_W, h: layout.topSectionH };
      case 'behaviorPanel':
        return { x: layout.rulesX, y: TITLE_H, w: RULES_PANEL_W, h: Math.min(layout.topSectionH, 5 * 62 + 40) };
      case 'saveButton':
        return { x: CANVAS_WIDTH / 2 - 60, y: layout.paletteY - 52, w: 120, h: 36 };
      default:
        return { x: CANVAS_WIDTH / 2 - 60, y: CANVAS_HEIGHT / 2 - 20, w: 120, h: 40 };
    }
  }
}
